import React from 'react';
import { ChevronRight } from 'lucide-react';
import { Skeleton } from '../../../components/ui/skeleton';
import { cn } from '../../../lib/utils';
import { CashflowCardsProps } from '../types';
import { formatNumber } from '../format';

interface StepProps {
  label: React.ReactNode;
  value: number;
  hint: React.ReactNode;
  isLoading: boolean;
}

/** Обычное звено цепочки: surface-карточка с крупным числом. */
function Step({ label, value, hint, isLoading }: StepProps) {
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-card border border-border-soft bg-surface px-[18px] py-4 shadow-card">
      <span className="text-[13px] text-text-2">{label}</span>
      {isLoading ? (
        <Skeleton className="h-8 w-32" />
      ) : (
        <b className="font-display text-2xl font-extrabold leading-tight tabular-nums">{formatNumber(value)}</b>
      )}
      <span className="text-xs text-text-2 tabular-nums">{hint}</span>
    </div>
  );
}

function Arrow({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn('hidden place-items-center text-text-3 lg:grid', className)}>
      <ChevronRight className="size-5" />
    </span>
  );
}

/**
 * Цепочка сверки по всем провайдерам в сумах: Собрано → Чистое → Поступило → Осталось получить.
 * Последнее звено — долг за всё время, поэтому оно тёмное (hero) и не зависит от периода.
 */
export function CashflowCards({ total, hasForeign, periodLabel, isLoading }: CashflowCardsProps) {
  const commission = total.gross - total.net;
  return (
    <section
      aria-label="Цепочка сверки"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_28px_minmax(0,1fr)_28px_minmax(0,1fr)_28px_minmax(0,1.15fr)] lg:gap-1.5"
    >
      <Step label="Собрано" value={total.gross} hint={`до комиссии, ${periodLabel}`} isLoading={isLoading} />
      <Arrow />
      <Step
        label="Чистое"
        value={total.net}
        hint={
          commission > 0 ? `комиссия −${formatNumber(commission)}` : total.gross > 0 ? 'без комиссии' : 'после комиссии'
        }
        isLoading={isLoading}
      />
      <Arrow />
      <Step label="Поступило" value={total.settled} hint={`по выписке и вручную, ${periodLabel}`} isLoading={isLoading} />
      <Arrow />
      <div className="flex min-w-0 flex-col gap-2 rounded-card bg-hero px-[18px] py-4 text-on-hero">
        <span className="flex items-center justify-between gap-2">
          <span className="text-[13px] text-on-hero-2">Осталось получить</span>
          <span className="inline-flex h-[22px] items-center rounded-full bg-accent px-2 text-[11px] font-bold text-on-accent">
            за всё время
          </span>
        </span>
        {isLoading ? (
          <Skeleton className="h-9 w-36 bg-hero-track" />
        ) : (
          <b className="font-display text-[28px] font-extrabold leading-tight tabular-nums">
            {formatNumber(total.outstanding)}
            <span className="ml-1.5 text-sm font-semibold">сум</span>
          </b>
        )}
        <span className="text-xs text-on-hero-2">
          Долг провайдеров не зависит от периода.
          {hasForeign && ' Доллары Apple — по последнему Курсу'}
        </span>
      </div>
    </section>
  );
}
